import type {
  FunnelWizardState,
  FunnelGraph,
  FunnelGraphNode,
  FunnelGraphEdge,
  FunnelStageConfig,
  TrafficChannel,
} from "./schema";
import { getStageTypeMeta } from "./stageTypes";

export const STAGE_COLORS: Record<string, string> = {
  "blog-post":      "#3b82f6",
  "landing-page":   "#10b981",
  "opt-in-page":    "#8b5cf6",
  "email-sequence": "#f59e0b",
  "discovery-call": "#0ea5e9",
  "proposal":       "#6366f1",
  "sales-page":     "#ef4444",
  "order-form":     "#22c55e",
  "upsell":         "#f97316",
  "webinar":        "#a855f7",
  "application":    "#14b8a6",
  "retainer-close": "#16a34a",
  "custom":         "#6b7280",
};

export const STAGE_ICONS: Record<string, string> = {
  "blog-post":      "BP",
  "landing-page":   "LP",
  "opt-in-page":    "OP",
  "email-sequence": "EM",
  "discovery-call": "DC",
  "proposal":       "PR",
  "sales-page":     "SP",
  "order-form":     "OF",
  "upsell":         "UP",
  "webinar":        "WB",
  "application":    "AP",
  "retainer-close": "RC",
  "custom":         "ST",
};

const DEFAULT_STAGE_LAYOUT_WIDTH = 340;
const STAGE_LAYOUT_START_X = 300;
const STAGE_LAYOUT_GAP_X = 70;

export function stageColor(type?: string): string {
  if (STAGE_COLORS[type ?? "custom"]) return STAGE_COLORS[type ?? "custom"]!;
  const group = getStageTypeMeta(type ?? "custom").group;
  const groupColors: Record<string, string> = {
    acquisition: "#3b82f6",
    nurture: "#f59e0b",
    conversion: "#22c55e",
    retention: "#14b8a6",
    plg: "#8b5cf6",
    utility: "#64748b",
  };
  return groupColors[group] ?? "#6b7280";
}

export function stageIcon(type?: string): string {
  return STAGE_ICONS[type ?? "custom"] ?? getStageTypeMeta(type ?? "custom").icon ?? "ST";
}

export function stageLabel(type?: string): string {
  if (!type || type === "custom") return "Custom";
  return type.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

export function buildInitialGraph(state: FunnelWizardState): FunnelGraph {
  const { stages, trafficChannels } = state;
  const nodes: FunnelGraphNode[] = [];
  const edges: FunnelGraphEdge[] = [];

  const activeChannels = trafficChannels.filter(c => c.monthlyVisitors > 0);
  const srcCount = Math.max(activeChannels.length, 1);
  const srcSpacing = 160;
  const srcTotalHeight = (srcCount - 1) * srcSpacing;

  if (activeChannels.length > 0) {
    activeChannels.forEach((ch, i) => {
      nodes.push({
        id: `src-${ch.id}`,
        type: "trafficSource",
        position: { x: 20, y: i * srcSpacing },
        data: {
          label: ch.label,
          stageType: "custom",
          conversionRate: 100,
          channel: ch.id,
          isSource: true,
          monthlyVisitors: ch.monthlyVisitors,
          publishedStatus: "draft",
          assetStatus: "draft",
          trackingStatus: "not_set",
          waitDays: 0,
          requiredStage: true,
        },
      });
    });
  } else {
    nodes.push({
      id: "src-organic",
      type: "trafficSource",
      position: { x: 20, y: 0 },
      data: {
        label: "Organic Traffic",
        stageType: "custom",
        conversionRate: 100,
        channel: "google_organic",
        isSource: true,
        monthlyVisitors: state.projectionInputs.monthlyVisitors,
        publishedStatus: "draft",
        assetStatus: "draft",
        trackingStatus: "not_set",
        waitDays: 0,
        requiredStage: true,
      },
    });
  }

  const maxStageWidth = Math.max(
    DEFAULT_STAGE_LAYOUT_WIDTH,
    ...stages.map((stage) => (
      typeof stage.boxWidth === "number" && Number.isFinite(stage.boxWidth)
        ? stage.boxWidth
        : DEFAULT_STAGE_LAYOUT_WIDTH
    )),
  );
  const stageStartX = STAGE_LAYOUT_START_X;
  const stageGapX = maxStageWidth + STAGE_LAYOUT_GAP_X;
  const stageCenterY = srcTotalHeight / 2;

  stages.forEach((stage, i) => {
    const id = `stage-${stage.id}`;
    nodes.push({
      id,
      type: "stage",
      position: { x: stageStartX + i * stageGapX, y: stageCenterY - 75 },
      data: {
        label: stage.name || `Stage ${i + 1}`,
        stageType: stage.stageType ?? "custom",
        conversionRate: stage.conversionRate,
        channel: undefined,
        isSource: false,
        url: stage.url,
        publishedStatus: stage.publishedStatus,
        owner: stage.owner,
        assetStatus: stage.assetStatus,
        trackingStatus: stage.trackingStatus,
        waitDays: stage.waitDays,
        dueInDays: stage.dueInDays,
        actualCvr: stage.actualCvr,
        actualVolume: stage.actualVolume,
        actualPeriodStart: stage.actualPeriodStart,
        actualPeriodEnd: stage.actualPeriodEnd,
        actualPeriodDays: stage.actualPeriodDays ?? 30,
        requiredStage: stage.requiredStage,
        assetUrl: stage.assetUrl,
        assetType: stage.assetType,
        assetSourceSystem: stage.assetSourceSystem,
        assetThumbnailUrl: stage.assetThumbnailUrl,
        assetExecutionStatus: stage.assetExecutionStatus ?? "unassigned",
        assetCtaNotes: stage.assetCtaNotes,
        color: stage.color,
        boxWidth: stage.boxWidth,
        boxHeight: stage.boxHeight,
      },
    });
  });

  // Sources → first stage
  const firstStageId = stages.length > 0 ? `stage-${stages[0].id}` : null;
  if (firstStageId) {
    const srcIds = activeChannels.length > 0
      ? activeChannels.map(c => `src-${c.id}`)
      : ["src-organic"];

    srcIds.forEach(srcId => {
      edges.push({ id: `e-${srcId}-${firstStageId}`, source: srcId, target: firstStageId, edgeType: "direct", splitPercent: 100, waitDays: 0 });
    });

    // Sequential stage → stage edges
    for (let i = 0; i < stages.length - 1; i++) {
      edges.push({
        id: `e-stage-${i}-${i + 1}`,
        source: `stage-${stages[i].id}`,
        target: `stage-${stages[i + 1].id}`,
        edgeType: "direct",
        splitPercent: 100,
        waitDays: 0,
      });
    }
  }

  return { nodes, edges };
}

export function computeGraphVolumes(
  nodes: FunnelGraphNode[],
  edges: FunnelGraphEdge[],
  trafficChannels: TrafficChannel[],
  fallbackVisitors: number,
): Map<string, number> {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const forwardEdges = edges.filter((edge) => !isFeedbackEdge(edge, nodeById));
  const feedbackEdges = edges.filter((edge) => isFeedbackEdge(edge, nodeById));

  const baseVolumes = computeForwardGraphVolumes(
    nodes,
    forwardEdges,
    trafficChannels,
    fallbackVisitors,
    new Map(),
  );

  const feedbackInputs = new Map<string, number>();
  for (const edge of feedbackEdges) {
    const sourceVolume = baseVolumes.get(edge.source) ?? 0;
    if (sourceVolume <= 0) continue;
    const contribution = sourceVolume * edgeSplit(edge) * edgeConversion(edge);
    feedbackInputs.set(edge.target, (feedbackInputs.get(edge.target) ?? 0) + contribution);
  }

  return computeForwardGraphVolumes(
    nodes,
    forwardEdges,
    trafficChannels,
    fallbackVisitors,
    feedbackInputs,
  );
}

function computeForwardGraphVolumes(
  nodes: FunnelGraphNode[],
  edges: FunnelGraphEdge[],
  trafficChannels: TrafficChannel[],
  fallbackVisitors: number,
  additionalInputs: Map<string, number>,
): Map<string, number> {
  const volumes = new Map<string, number>();
  const sortedNodes = [...nodes].sort((a, b) => {
    if (a.data.isSource !== b.data.isSource) return a.data.isSource ? -1 : 1;
    if (a.position.x !== b.position.x) return a.position.x - b.position.x;
    return a.position.y - b.position.y;
  });

  for (const node of sortedNodes) {
    if (node.data.isSource) {
      const channel = trafficChannels.find(c => c.id === node.data.channel);
      volumes.set(node.id, channel?.monthlyVisitors ?? node.data.monthlyVisitors ?? fallbackVisitors);
      continue;
    }

    const parentEdges = edges.filter((edge) => edge.target === node.id);
    const totalIn = parentEdges.reduce((sum, edge) => {
      const parentVolume = volumes.get(edge.source) ?? 0;
      return sum + parentVolume * edgeSplit(edge) * edgeConversion(edge);
    }, additionalInputs.get(node.id) ?? 0);
    volumes.set(node.id, Math.round(totalIn * nodeConversion(node)));
  }

  return volumes;
}

function edgeSplit(edge: FunnelGraphEdge): number {
  const type = edge.edgeType ?? "direct";
  if (["branch", "retarget", "skip", "loop", "yes", "no", "split", "optional", "retargeting", "wait", "survey"].includes(type)) {
    return (edge.splitPercent ?? 100) / 100;
  }
  return 1;
}

function edgeConversion(edge: FunnelGraphEdge): number {
  return edge.conversionRate == null && edge.edgeCvr == null
    ? 1
    : (edge.conversionRate ?? edge.edgeCvr ?? 100) / 100;
}

function nodeConversion(node: FunnelGraphNode): number {
  if (node.data.isSource) return 1;
  if (node.type === "branch" || node.type === "wait" || node.type === "surveySplit" || node.type === "retargeting") {
    return 1;
  }
  return (node.data.conversionRate ?? 10) / 100;
}

function isFeedbackEdge(edge: FunnelGraphEdge, nodeById: Map<string, FunnelGraphNode>): boolean {
  if (edge.edgeType === "loop" || edge.edgeType === "retarget" || edge.edgeType === "retargeting") return true;
  const source = nodeById.get(edge.source);
  const target = nodeById.get(edge.target);
  if (!source || !target) return false;
  return source.position.x >= target.position.x;
}

export type GraphStageVolumeRow = {
  nodeId: string;
  stageId: string;
  stageName: string;
  stageType: string;
  volume: number;
};

export function computeGraphEntryVisitors(
  graph: FunnelGraph,
  trafficChannels: TrafficChannel[],
  fallbackVisitors: number,
): number {
  const sourceNodes = graph.nodes.filter((node) => node.data.isSource);
  if (sourceNodes.length === 0) return fallbackVisitors;
  return sourceNodes.reduce((sum, node) => {
    const channel = trafficChannels.find((item) => item.id === node.data.channel);
    return sum + (channel?.monthlyVisitors ?? node.data.monthlyVisitors ?? 0);
  }, 0) || fallbackVisitors;
}

export function computeGraphStageVolumeRows(
  graph: FunnelGraph,
  trafficChannels: TrafficChannel[],
  fallbackVisitors: number,
): GraphStageVolumeRow[] {
  const volumes = computeGraphVolumes(graph.nodes, graph.edges, trafficChannels, fallbackVisitors);
  const stageNodesById = new Map(graph.nodes.filter((node) => node.type === "stage").map((node) => [node.id, node]));
  return graphToStages(graph.nodes, graph.edges).map((stage) => {
    const nodeId = stageNodesById.has(`stage-${stage.id}`) ? `stage-${stage.id}` : stage.id;
    const node = stageNodesById.get(nodeId);
    return {
      nodeId,
      stageId: stage.id,
      stageName: stage.name,
      stageType: stage.stageType,
      volume: volumes.get(nodeId) ?? 0,
      ...(node ? { stageName: node.data.label || stage.name, stageType: node.data.stageType ?? stage.stageType } : {}),
    };
  });
}

export function graphToStages(
  nodes: FunnelGraphNode[],
  edges: FunnelGraphEdge[],
): FunnelStageConfig[] {
  const stageNodes = nodes.filter(n => n.type === "stage");
  const stageIds = new Set(stageNodes.map(n => n.id));

  const indegree = new Map<string, number>();
  const outgoing = new Map<string, string[]>();

  for (const n of stageNodes) {
    indegree.set(n.id, 0);
    outgoing.set(n.id, []);
  }
  for (const e of edges) {
    if (stageIds.has(e.target) && stageIds.has(e.source)) {
      indegree.set(e.target, (indegree.get(e.target) ?? 0) + 1);
      outgoing.get(e.source)?.push(e.target);
    } else if (stageIds.has(e.target)) {
      // edge from traffic source — counts as entry, no indegree bump for stage-to-stage sort
    }
  }

  const sorted: FunnelGraphNode[] = [];
  const queue = stageNodes.filter(n => (indegree.get(n.id) ?? 0) === 0);

  while (queue.length > 0) {
    const n = queue.shift()!;
    sorted.push(n);
    for (const nextId of (outgoing.get(n.id) ?? [])) {
      const d = (indegree.get(nextId) ?? 1) - 1;
      indegree.set(nextId, d);
      if (d === 0) {
        const next = stageNodes.find(s => s.id === nextId);
        if (next) queue.push(next);
      }
    }
  }

  for (const n of stageNodes) {
    if (!sorted.some(s => s.id === n.id)) sorted.push(n);
  }

  return sorted.map(n => ({
    id: n.id.startsWith("stage-") ? n.id.slice(6) : n.id,
    name: n.data.label || "Unnamed",
    conversionRate: Math.max(0.1, n.data.conversionRate ?? 10),
    isEditable: true,
    stageType: n.data.stageType ?? "custom",
    note: undefined,
    url: n.data.url,
    color: n.data.color,
    boxWidth: n.data.boxWidth,
    boxHeight: n.data.boxHeight,
    publishedStatus: n.data.publishedStatus ?? "draft",
    actualCvr: n.data.actualCvr,
    actualVolume: n.data.actualVolume,
    actualPeriodStart: n.data.actualPeriodStart,
    actualPeriodEnd: n.data.actualPeriodEnd,
    actualPeriodDays: n.data.actualPeriodDays ?? 30,
    requiredStage: n.data.optionalStage != null ? !n.data.optionalStage : n.data.requiredStage ?? true,
    owner: n.data.owner,
    assetStatus: n.data.assetStatus ?? "draft",
    assetUrl: n.data.assetUrl,
    assetType: n.data.assetType,
    assetSourceSystem: n.data.assetSourceSystem,
    assetThumbnailUrl: n.data.assetThumbnailUrl,
    assetExecutionStatus: n.data.assetExecutionStatus ?? "unassigned",
    assetCtaNotes: n.data.assetCtaNotes,
    trackingStatus: n.data.trackingStatus ?? "not_set",
    waitDays: n.data.waitDays ?? 0,
    dueInDays: n.data.dueInDays,
    priority: "medium" as const,
  }));
}
